import { createContext, useContext, ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from './AuthContext';
import { organizationsService } from '../services/firebase/organizations.service';
import { Organization, OrganizationMember } from '../types/organization.types';

interface OrganizationContextType {
  organization: Organization | null;
  members: OrganizationMember[];
  loading: boolean;
  refreshOrganization: () => Promise<void>;
  refreshMembers: () => Promise<void>;
  updateOrganization: (data: Partial<Organization>) => Promise<void>;
  canWrite: boolean;
  canManageTeam: boolean;
  isOwner: boolean;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

interface OrganizationProviderProps {
  children: ReactNode;
}

export function OrganizationProvider({ children }: OrganizationProviderProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const organizationId = user?.organizationId;

  // Fetch organization data with React Query caching
  const {
    data: organization = null,
    isLoading: isLoadingOrg,
  } = useQuery({
    queryKey: ['organization', organizationId],
    queryFn: async () => {
      if (!organizationId) return null;
      return organizationsService.getOrganization(organizationId);
    },
    enabled: !!organizationId,
  });

  // Fetch organization members with React Query caching
  const {
    data: members = [],
    isLoading: isLoadingMembers,
  } = useQuery({
    queryKey: ['organizationMembers', organizationId],
    queryFn: async () => {
      if (!organizationId) return [];
      return organizationsService.getOrganizationMembers(organizationId);
    },
    enabled: !!organizationId,
  });

  const loading = isLoadingOrg || isLoadingMembers;

  // Update organization mutation
  const updateOrgMutation = useMutation({
    mutationFn: async (data: Partial<Organization>) => {
      if (!organizationId) throw new Error('No organization to update');
      await organizationsService.updateOrganization(organizationId, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization', organizationId] });
    },
  });

  const refreshOrganization = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['organization', organizationId] });
  };

  const refreshMembers = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['organizationMembers', organizationId] });
  };

  const updateOrganization = async (data: Partial<Organization>): Promise<void> => {
    await updateOrgMutation.mutateAsync(data);
  };

  // Permission helpers
  const canWrite = user?.role === 'owner' || user?.role === 'admin';
  const canManageTeam = user?.role === 'owner' || user?.role === 'admin';
  const isOwner = user?.role === 'owner';

  const value: OrganizationContextType = {
    organization,
    members,
    loading,
    refreshOrganization,
    refreshMembers,
    updateOrganization,
    canWrite,
    canManageTeam,
    isOwner,
  };

  return <OrganizationContext.Provider value={value}>{children}</OrganizationContext.Provider>;
}

// Custom hook to use the organization context
export function useOrganization(): OrganizationContextType {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
}
